import InvoiceConfiguration from "./InvoiceConfiguration";
import BaseUser from "./BaseUser";
import DBObject from "../utils/DBObject";
import CloneContext from "../utils/CloneContext";
import AdministratorType from "../classes/AdministratorType";

export default class Administrator extends BaseUser {
  private static readonly _ADMINISTRATORTYPE: number = 0;
  private static readonly _EMAIL: number = 1;
  private static readonly _INVOICECONFIGURATION: number = 2;
  private static readonly _MOBILENUMBER: number = 3;
  private static readonly _NAME: number = 4;
  private static readonly _PASSWORD: number = 5;
  private _name: string = "";
  private _email: string = "";
  private _mobileNumber: string = "";
  private _password: string = "";
  private _administratorType: AdministratorType = AdministratorType.Admin;
  private _invoiceConfiguration: InvoiceConfiguration = null;
  public constructor(
    d3eParams?: Partial<{
      administratorType: AdministratorType;
      email: string;
      invoiceConfiguration: InvoiceConfiguration;
      mobileNumber: string;
      name: string;
      password: string;
    }>
  ) {
    super();

    this.setAdministratorType(
      d3eParams?.administratorType ?? AdministratorType.Admin
    );

    this.setEmail(d3eParams?.email ?? "");

    this.setInvoiceConfiguration(d3eParams?.invoiceConfiguration ?? null);

    this.setMobileNumber(d3eParams?.mobileNumber ?? "");

    this.setName(d3eParams?.name ?? "");

    this.setPassword(d3eParams?.password ?? "");
  }
  public get d3eType(): string {
    return "Administrator";
  }
  public static updateInvoiceConfigurationInverse(
    on: any,
    obj: any,
    type: string
  ): void {
    if ("Insert" === type) {
      on.addToInvoiceConfiguration(obj as InvoiceConfiguration);
    }

    if ("Delete" === type) {
      on.removeFromInvoiceConfiguration(obj as InvoiceConfiguration);
    }
  }
  public get name(): string {
    return this._name;
  }
  public setName(val: string): void {
    let isValChanged: boolean = this._name !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(Administrator._NAME, this._name);

    this._name = val;

    this.fire("name", this);
  }
  public get email(): string {
    return this._email;
  }
  public setEmail(val: string): void {
    let isValChanged: boolean = this._email !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(Administrator._EMAIL, this._email);

    this._email = val;

    this.fire("email", this);
  }
  public get mobileNumber(): string {
    return this._mobileNumber;
  }
  public setMobileNumber(val: string): void {
    let isValChanged: boolean = this._mobileNumber !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(Administrator._MOBILENUMBER, this._mobileNumber);

    this._mobileNumber = val;

    this.fire("mobileNumber", this);
  }
  public get password(): string {
    return this._password;
  }
  public setPassword(val: string): void {
    let isValChanged: boolean = this._password !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(Administrator._PASSWORD, this._password);

    this._password = val;

    this.fire("password", this);
  }
  public get administratorType(): AdministratorType {
    return this._administratorType;
  }
  public setAdministratorType(val: AdministratorType): void {
    let isValChanged: boolean = this._administratorType !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(
      Administrator._ADMINISTRATORTYPE,
      this._administratorType.index
    );

    this._administratorType = val;

    this.fire("administratorType", this);
  }
  public get invoiceConfiguration(): InvoiceConfiguration {
    return this._invoiceConfiguration;
  }
  public setInvoiceConfiguration(val: InvoiceConfiguration): void {
    let isValChanged: boolean = this._invoiceConfiguration !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(
      Administrator._INVOICECONFIGURATION,
      this._invoiceConfiguration
    );

    this.updateObservable(
      "invoiceConfiguration",
      this._invoiceConfiguration,
      val
    );

    this._invoiceConfiguration = val;

    this.fire("invoiceConfiguration", this);
  }
  public get(field: number): any {
    switch (field) {
      case Administrator._NAME: {
        return this._name;
      }

      case Administrator._EMAIL: {
        return this._email;
      }

      case Administrator._MOBILENUMBER: {
        return this._mobileNumber;
      }

      case Administrator._PASSWORD: {
        return this._password;
      }

      case Administrator._ADMINISTRATORTYPE: {
        return this._administratorType.index;
      }

      case Administrator._INVOICECONFIGURATION: {
        return this._invoiceConfiguration;
      }
      default: {
        return super.get(field);
      }
    }
  }
  public updateD3EChanges(index: number, value: any): void {
    if (this.lockedChanges()) {
      return;
    }

    super.updateD3EChanges(index, value);
  }
  public restore(): void {
    /*
TODO: Might be removed
*/

    this.d3eChanges.restore(this);
  }
  public deepClone(clearId = true): Administrator {
    let ctx: CloneContext = new CloneContext({ "clearId": clearId });

    return ctx.startClone(this);
  }
  public collectChildValues(ctx: CloneContext): void {
    super.collectChildValues(ctx);
  }
  public deepCloneIntoObj(dbObj: DBObject, ctx: CloneContext): void {
    super.deepCloneIntoObj(dbObj, ctx);

    let obj: Administrator = dbObj as Administrator;

    obj.setName(this._name);

    obj.setEmail(this._email);

    obj.setMobileNumber(this._mobileNumber);

    obj.setPassword(this._password);

    obj.setAdministratorType(this._administratorType);

    obj.setInvoiceConfiguration(this._invoiceConfiguration);
  }
  public set(field: number, value: any): void {
    switch (field) {
      case Administrator._NAME: {
        this.setName(value as string);
        break;
      }

      case Administrator._EMAIL: {
        this.setEmail(value as string);
        break;
      }

      case Administrator._MOBILENUMBER: {
        this.setMobileNumber(value as string);
        break;
      }

      case Administrator._PASSWORD: {
        this.setPassword(value as string);
        break;
      }

      case Administrator._ADMINISTRATORTYPE: {
        this.setAdministratorType(AdministratorType.values[value as number]);
        break;
      }

      case Administrator._INVOICECONFIGURATION: {
        this.setInvoiceConfiguration(value as InvoiceConfiguration);
        break;
      }
      default: {
        super.set(field, value);
      }
    }
  }
}
